import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import SearchBar from '../components/SearchBar';
import ProfileCard from '../components/ProfileCard';
import Footer from '../components/Footer';
import { supabase } from '../config/supabase';
import SafeIcon from '../common/SafeIcon';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight } = FiIcons;

// Helper function to shuffle an array
const shuffleArray = (array) => {
  const newArray = [...array]; // Create a copy to avoid mutating the original
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const HomePage = () => {
  const [allProfiles, setAllProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const resultsRef = useRef(null);
  const { isEnabled, features } = useFeatureFlags();

  useEffect(() => {
    fetchProfiles();
    fetchTags();
  }, []);

  const fetchProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAllProfiles(data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase.from('tags').select('*').order('name');
      if (error) throw error;
      setAllTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const filterProfiles = useCallback(() => {
    let filtered;

    if (!searchQuery && selectedTags.length === 0) {
      // Default view logic
      if (isEnabled('randomize_homepage')) {
        // If Randomize is ON: Show random selection from ALL approved profiles
        filtered = shuffleArray([...allProfiles]);
      } else {
        // If Randomize is OFF: Show only highlighted profiles, shuffled
        const highlighted = allProfiles.filter(profile => profile.is_highlighted);
        filtered = shuffleArray(highlighted);
      }
    } else {
      // Active search: Search through all profiles
      let searchResults = allProfiles;

      if (selectedTags.length > 0) {
        searchResults = searchResults.filter((profile) =>
          selectedTags.every((selectedTag) =>
            (profile.tags || []).some((profileTag) =>
              profileTag.toLowerCase() === selectedTag.toLowerCase()
            )
          )
        );
      }

      if (searchQuery) {
        searchResults = searchResults.filter(
          p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.instagram_handle.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      }
      filtered = searchResults;
    }

    // Limit displayed profiles to 25 to keep the page performant and clean
    setFilteredProfiles(filtered.slice(0, 25));
  }, [allProfiles, searchQuery, selectedTags, isEnabled, features]); // Added features/isEnabled dependencies

  useEffect(() => {
    const debounce = setTimeout(() => {
      filterProfiles();
    }, 300);

    return () => clearTimeout(debounce);
  }, [filterProfiles, searchQuery, selectedTags, features]); // Re-run when features change

  const handleTagSelect = useCallback((tag) => {
    // Overwrite selected tags with the new tag, allowing only one selection.
    setSelectedTags([tag]);
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleTagRemove = (tag) => {
    setSelectedTags((prevTags) => prevTags.filter((t) => t !== tag));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="container mx-auto px-4 py-8 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-6xl font-extrabold text-slate-900 mb-4 tracking-tight">
            Find Them Faster
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            The definitive directory of Chaldean businesses, professionals, and creators on Instagram.
          </p>
        </motion.div>

        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedTags={selectedTags}
          onTagRemove={handleTagRemove}
          allTags={allTags}
          onTagSelect={handleTagSelect}
        />

        <div ref={resultsRef} className="scroll-mt-8 mt-12">
          {filteredProfiles.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredProfiles.map((profile) => (
                <ProfileCard key={profile.id} profile={profile} onTagClick={handleTagSelect} />
              ))}
            </motion.div>
          )}

          {filteredProfiles.length === 0 && !loading && (searchQuery || selectedTags.length > 0) && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No profiles found</h3>
              <p className="text-slate-600">Try adjusting your search criteria or tags.</p>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;